import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AdminCard from "../AdminCard";
import ImportModal from "./ImportModal";

export const RecipientsPanel = ({ groups, groupId, onChanged }) => {
    const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [form, setForm] = useState({ email: "", name: "" });
    const [showImport, setShowImport] = useState(false);
    const real = groups.filter((g) => !g.system);
    const byId = Object.fromEntries(real.map((g) => [g.id, g]));

    const load = useCallback(() => api.get("/mailing/recipients", { params: { group_id: groupId, q, page } }).then((r) => setData(r.data)), [groupId, q, page]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [groupId, q]);
    const refresh = () => { load(); onChanged(); };

    const add = async (e) => {
        e.preventDefault();
        try {
            const { data: r } = await api.post("/mailing/recipients", { ...form, group_ids: groupId ? [groupId] : [] });
            toast.success(r.status === "added" ? "Dodano adres." : "Adres już istniał – zaktualizowano grupy.");
            setForm({ email: "", name: "" }); refresh();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };
    const toggleGroup = async (r, gid) => {
        const group_ids = r.group_ids.includes(gid) ? r.group_ids.filter((x) => x !== gid) : [...r.group_ids, gid];
        await api.put(`/mailing/recipients/${r.id}`, { group_ids }); refresh();
    };
    const resubscribe = async (r) => { await api.put(`/mailing/recipients/${r.id}`, { unsubscribed: false }); toast.success("Przywrócono adres."); refresh(); };
    const del = async (r) => { if (!window.confirm(`Usunąć ${r.email}?`)) return; await api.delete(`/mailing/recipients/${r.id}`); refresh(); };
    const pages = Math.max(1, Math.ceil(data.total / data.page_size));

    return (
        <AdminCard>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="font-serif text-xl text-[#092E22]">Adresaci {groupId ? `· ${byId[groupId]?.name || ""}` : ""} ({data.total})</h3>
                <Button onClick={() => setShowImport(true)} variant="outline" className="rounded-full" data-testid="mailing-import-open"><Upload size={14} className="mr-2" />Import CSV</Button>
            </div>
            <form onSubmit={add} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 mb-4" data-testid="mailing-recipient-form">
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="adres@email.pl" data-testid="mailing-recipient-email" />
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Imię i nazwisko (opcjonalnie)" data-testid="mailing-recipient-name" />
                <Button type="submit" className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid="mailing-recipient-add"><Plus size={14} className="mr-1" />Dodaj</Button>
            </form>
            <div className="relative mb-3">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj po adresie lub nazwisku…" className="pl-9" data-testid="mailing-recipient-search" />
            </div>
            <div className="divide-y divide-gray-50" data-testid="mailing-recipient-list">
                {data.items.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">Brak adresów.</p>}
                {data.items.map((r) => (
                    <div key={r.id} className="py-3 flex flex-col md:flex-row md:items-center gap-2" data-testid={`mailing-recipient-${r.email}`}>
                        <div className="min-w-0 md:w-64">
                            <p className={`text-sm truncate ${r.unsubscribed ? "line-through text-gray-400" : "text-[#092E22]"}`}>{r.email}</p>
                            {r.name && <p className="text-xs text-gray-500 truncate">{r.name}</p>}
                        </div>
                        <div className="flex flex-wrap gap-1 flex-1">
                            {real.map((g) => (
                                <button key={g.id} onClick={() => toggleGroup(r, g.id)} data-testid={`mailing-recipient-${r.id}-group-${g.id}`}
                                    className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${r.group_ids.includes(g.id) ? "text-white border-transparent" : "text-gray-400 border-gray-200 hover:border-gray-300"}`}
                                    style={r.group_ids.includes(g.id) ? { background: g.color } : undefined}>{g.name}</button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {r.unsubscribed && <button onClick={() => resubscribe(r)} className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600" data-testid={`mailing-recipient-resub-${r.id}`} title="Kliknij, aby przywrócić">wypisany</button>}
                            <button onClick={() => del(r)} className="text-red-400 hover:text-red-600 p-1" data-testid={`mailing-recipient-delete-${r.id}`}><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>
            {pages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-4 text-sm">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full">‹</Button>
                    <span data-testid="mailing-recipient-page">{page} / {pages}</span>
                    <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-full">›</Button>
                </div>
            )}
            {showImport && <ImportModal groups={groups} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); refresh(); }} />}
        </AdminCard>
    );
};

export default RecipientsPanel;
